import axios from "axios";
import {
  IConsultor,
  IConsultorFilter,
  IConsultorProyectoData,
} from "constants/interfaces/consultor.interface";
import { IPagination } from "constants/interfaces/pagination.interface";
import { getAuthToken } from "helpers/Auth";

/**
 * Método que obtiene la lista de consultores.
 *
 * @param {IConsultorFilter} filters Filtros de búsqueda.
 * @param {IPagination} pagination Paginación.
 * @returns Lista de consultores.
 */
export const getConsultores = async (
  filters: IConsultorFilter,
  pagination: IPagination
) => {
  try {
    let filter = "";
    if (filters.nombre) filter += `&NOMBRE=${filters.nombre}`;
    if (filters.nroRegistro) filter += `&NroRegistro=${filters.nroRegistro}`;
    if (filters.region !== "0" && filters.region)
      filter += `&REGIONID=${filters.region}`;
    if (filters.vigente !== 2) filter += `&VIGENTE=${filters.vigente}`;

    const response = await axios.get(
      `${process.env.REACT_APP_GCR_URL}/consultores?page=${pagination.page}&limit=${pagination.limit}${filter}`,
      {
        headers: {
          "x-api-key": `Basic ${getAuthToken()}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Método encargado de descargar un archivo excel con los datos de los consultores.
 *
 * @param {IConsultorFilter} filters Filtros de búsqueda.
 * @returns Archivo excel con los datos de los consultores.
 */
export const downloadExcelFile = async (filters: IConsultorFilter) => {
  try {
    let filter = "";
    if (filters.nombre)
      filter += (filter ? "&" : "?") + `NOMBRE=${filters.nombre}`;
    if (filters.nroRegistro)
      filter += (filter ? "&" : "?") + `NroRegistro=${filters.nroRegistro}`;
    if (filters.region)
      if (filters.region !== "0")
        filter += (filter ? "&" : "?") + `REGIONID=${filters.region}`;
    if (filters.vigente !== 2)
      filter += (filter ? "&" : "?") + `VIGENTE=${filters.vigente}`;

    const response = await axios.get(
      `${process.env.REACT_APP_GCR_URL}/consultores/descargarExcel${filter}`,
      {
        responseType: "blob",
        headers: {
          "x-api-key": `Basic ${getAuthToken()}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Obtiene los datos de un consultor por su ID
 *
 * @param {string} id ID del consultor.
 * @return {Promise<IConsultor>} Datos del consultor.
 */
export const getConsultorDataById = async (id: string): Promise<IConsultor> => {
  try {
    if (!id) throw new Error("No se ha proporcionado un ID");

    const response = await axios.get(
      `${process.env.REACT_APP_GCR_URL}/consultores/getConsultorDetalle?PersonaID=${id}`,
      {
        headers: {
          "x-api-key": `Basic ${getAuthToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Método que obtiene el listado de proyectos de un consultor a través de un rut.
 *
 * @param {string} rut Rut del consultor.
 * @returns {Promise<IConsultorProyectoData[]>}
 */
export const getProyectosConsultor = async (
  rut: string
): Promise<IConsultorProyectoData[]> => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_GCR_URL}/httpService/resultadoProyectos?RUT=${rut}`,
      {
        headers: {
          "x-api-key": `Basic ${getAuthToken()}`,
        },
      }
    );

    const data = response.data.map((item: IConsultorProyectoData) => ({
      "Total Proyectos Postulados": item.TotalProyectosPostulados,
      "Proyectos Seleccionados": item.EstadoProyectoSeleccionado,
      "Proyectos No Seleccionados": item.EstadoProyectoNoSeleccionado,
      "Proyectos Admitidos": item.EstadoProyectoAdmitido,
      "Proyectos No Admitidos": item.EstadoProyectoNoAdmitido,
      "Proyectos Retirados": item.EstadoProyectoRetirado,
      "Año Resolucion Concurso": item.AnioResolConcurso,
    }));

    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};


/**
 * Método que descarga el pdf de los detalles consultores.
 *
 * @param {string} PersonaID del consultor.
 * @returns {Promise<Blob>} Archivo PDF.
 */
export const descargarPDF = async (IdPersona: string) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_GCR_URL}/pdf/ObtenerPdfPersona?IdPersona=${IdPersona}`,
      {
        responseType: "blob",
        headers: {
          "x-api-key": `Basic ${getAuthToken()}`,
        },
      }
    );

    //URL DEL ARCHIVO
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;

    link.setAttribute("download", `Detalle_Consultor.pdf`);
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

